import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, Home, BookOpen, Target, FileText, Dumbbell } from "lucide-react";

interface SmartBreadcrumbProps {
  currentPath: string;
  currentTopicId?: string;
  currentMode?: 'practice' | 'notes' | 'study' | 'mock';
}

export function SmartBreadcrumb({ currentPath, currentTopicId, currentMode }: SmartBreadcrumbProps) {
  const navigate = useNavigate();

  const { data: topics = [] } = useQuery({
    queryKey: ["topics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("topics")
        .select("id, name")
        .order("name", { ascending: true });
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const currentTopic = topics.find(t => t.id === currentTopicId);

  // Determine breadcrumb structure based on current path
  const isOnStudy = currentPath.startsWith('/study');
  const isOnPractice = currentPath.startsWith('/practice');
  const isOnNotes = currentPath.startsWith('/notes');
  const isOnMock = currentPath.startsWith('/mock');

  const studyModes = [
    { label: 'Study Hub', path: '/study', icon: BookOpen },
    { label: 'Practice MCQs', path: '/practice', icon: Target },
    { label: 'Notes', path: '/notes', icon: FileText },
    { label: 'Mock Exam', path: '/mock', icon: Dumbbell },
  ];

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {/* Home */}
        <BreadcrumbItem>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 hover:text-foreground transition-colors">
              <Home className="h-3 w-3" />
              <span>Home</span>
              <ChevronDown className="h-3 w-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="z-50 bg-background">
              <DropdownMenuItem onClick={() => navigate('/')}>
                <Home className="mr-2 h-4 w-4" />
                Landing
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                Dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/pricing')}>
                Pricing
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </BreadcrumbItem>

        {/* Study Mode Selector */}
        {(isOnStudy || isOnPractice || isOnNotes || isOnMock) && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 hover:text-foreground transition-colors">
                  <BookOpen className="h-3 w-3" />
                  <span>
                    {isOnPractice && 'Practice'}
                    {isOnNotes && 'Notes'}
                    {isOnMock && 'Mock Exam'}
                    {isOnStudy && !currentTopicId && 'Study Hub'}
                    {isOnStudy && currentTopicId && 'Study'}
                  </span>
                  <ChevronDown className="h-3 w-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="z-50 bg-background">
                  {studyModes.map((mode) => {
                    const Icon = mode.icon;
                    return (
                      <DropdownMenuItem key={mode.path} onClick={() => navigate(mode.path)}>
                        <Icon className="mr-2 h-4 w-4" />
                        {mode.label}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </BreadcrumbItem>
          </>
        )}

        {/* Topic Selector */}
        {currentTopic && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 hover:text-foreground transition-colors">
                  <span className="max-w-[150px] truncate">{currentTopic.name}</span>
                  <ChevronDown className="h-3 w-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="max-h-[300px] overflow-y-auto z-50 bg-background">
                  <DropdownMenuItem onClick={() => {
                    const basePath = isOnPractice ? '/practice' : isOnNotes ? '/notes' : '/study';
                    navigate(basePath);
                  }}>
                    <span className="font-medium">All Topics</span>
                  </DropdownMenuItem>
                  {topics.map((topic) => (
                    <DropdownMenuItem 
                      key={topic.id} 
                      onClick={() => {
                        const basePath = isOnPractice ? '/practice' : isOnNotes ? '/notes' : '/study';
                        navigate(`${basePath}?topic=${topic.id}`);
                      }}
                    >
                      {topic.id === currentTopicId && (
                        <Badge variant="secondary" className="mr-2 h-4 w-4 p-0 flex items-center justify-center">
                          ✓
                        </Badge>
                      )}
                      <span className={topic.id === currentTopicId ? 'font-medium' : ''}>
                        {topic.name}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </BreadcrumbItem>
          </>
        )}

        {/* Current Page (non-clickable) */}
        {currentMode && currentTopic && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-medium">
                {currentMode === 'practice' && 'Practice'}
                {currentMode === 'notes' && 'Notes'}
                {currentMode === 'study' && 'Study'}
                {currentMode === 'mock' && 'Mock Exam'}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
